import { log } from "@app/app/services/log";
import { createBrowserHistory, History, Location } from "history";

import { UrlAdapter } from "../types";

/**
 * Basic adapter implementation using the history library for URL management
 * Provides a clean interface for state management through URL parameters
 */
export class BasicUrlAdapter implements UrlAdapter {
    private history: History;
    private stateChangeCallbacks: Set<(state: Record<string, unknown>) => void> = new Set();

    public constructor() {
        log.debug("[BasicUrlAdapter] Initializing with browser history");
        this.history = createBrowserHistory();

        // Listen for history changes
        this.history.listen(() => {
            log.debug("[BasicUrlAdapter] History change detected");
            this.notifyStateChange();
        });
    }

    public getState(): Record<string, unknown> {
        log.debug("[BasicUrlAdapter] Getting current state");
        const searchParams = new URLSearchParams(this.history.location.search);
        const state: Record<string, unknown> = {};

        for (const [key, value] of searchParams.entries()) {
            try {
                // Try to parse JSON values
                state[key] = JSON.parse(value);
            } catch {
                // If parsing fails, use the raw value
                state[key] = value;
            }
        }

        log.debug("[BasicUrlAdapter] Retrieved state", { state });
        return state;
    }

    public updateState(state: Record<string, unknown>, options?: { replace?: boolean; merge?: boolean }): void {
        log.debug("[BasicUrlAdapter] Updating state", { state, options });
        const searchParams = new URLSearchParams(this.history.location.search);
        const currentState = options?.merge ? this.getState() : {};
        const newState = { ...currentState, ...state };

        // Update URL search parameters
        for (const [key, value] of Object.entries(newState)) {
            if (value === undefined || value === null) {
                log.debug("[BasicUrlAdapter] Removing parameter", { key });
                searchParams.delete(key);
            } else {
                log.debug("[BasicUrlAdapter] Setting parameter", { key, value });
                searchParams.set(key, JSON.stringify(value));
            }
        }

        // Create new location object
        const newLocation: Partial<Location> = {
            pathname: this.history.location.pathname,
            search: searchParams.toString() ? `?${searchParams.toString()}` : "",
            hash: this.history.location.hash,
        };

        // Update URL using history methods
        if (options?.replace) {
            log.debug("[BasicUrlAdapter] Replacing history state");
            this.history.replace(newLocation);
        } else {
            log.debug("[BasicUrlAdapter] Pushing new history state");
            this.history.push(newLocation);
        }
    }

    public onStateChange(callback: (state: Record<string, unknown>) => void): () => void {
        log.debug("[BasicUrlAdapter] Registering state change callback");
        this.stateChangeCallbacks.add(callback);

        // Return unsubscribe function
        return () => {
            log.debug("[BasicUrlAdapter] Unregistering state change callback");
            this.stateChangeCallbacks.delete(callback);
        };
    }

    private notifyStateChange(): void {
        const state = this.getState();
        log.debug("[BasicUrlAdapter] Notifying state change", { state, callbackCount: this.stateChangeCallbacks.size });
        this.stateChangeCallbacks.forEach((callback) => callback(state));
    }
}
